/**
 * photo_downscale_controller.js — shrink the photos in a file input before a plain form posts them.
 *
 * Hosts on an <input type="file" multiple>. A phone photo is several megabytes
 * and nginx caps a whole request (infra/nginx/chippieapp.conf), so a handful
 * of originals would be refused before the app sees them. Each picked photo is
 * redrawn at most MAX_EDGE pixels on its longest side as a JPEG (shrinkPhoto,
 * _photo_shrink.js) and put back into the input, so the form posts the smaller
 * files under the same field name and the server reads them unchanged.
 *
 * The input's own validity is the lock: while photos are being prepared, or
 * when more than data-photo-downscale-max-value are picked, a custom validity
 * message stops the browser submitting the form. The server enforces the same
 * limit (MAX_ENQUIRY_PHOTOS in application/schemas/lead_schema.py), which the
 * template passes in, so this is a courtesy, not the rule.
 *
 * A photo this browser cannot decode (HEIC outside Safari), or one the redraw
 * would not make smaller, is kept as picked; the server's size limit still
 * applies to it.
 *
 * Wired with data-controller="photo-downscale" on the file input.
 */

import { Controller } from "@hotwired/stimulus";
import { shrinkPhoto } from "./_photo_shrink.js";

// Longest side, in pixels, of a photo posted with a plain form: several go in
// one request, so smaller than a held photo's default.
const MAX_EDGE = 1600;
const PREPARING_MESSAGE = "Preparing your photos, one moment...";

export default class extends Controller {
    static values = { max: Number };

    connect() {
        this._run = 0;
        this._onChange = () => this.prepare();
        this.element.addEventListener("change", this._onChange);
    }

    disconnect() {
        this.element.removeEventListener("change", this._onChange);
    }

    async prepare() {
        const input = this.element;
        const files = Array.from(input.files);
        const run = ++this._run;

        if (files.length > this.maxValue) {
            input.setCustomValidity(`Please attach up to ${this.maxValue} photos.`);
            input.reportValidity();
            return;
        }

        input.setCustomValidity(PREPARING_MESSAGE);
        try {
            const shrunk = await Promise.all(files.map((file) => shrinkPhoto(file, MAX_EDGE)));
            // A newer pick replaced this one while it was being prepared.
            if (run !== this._run) return;
            const transfer = new DataTransfer();
            shrunk.forEach((file) => transfer.items.add(file));
            input.files = transfer.files;
        } catch {
            // No DataTransfer constructor: the originals stay in the input.
        } finally {
            if (run === this._run) input.setCustomValidity("");
        }
    }
}
